import { ArrowRightOutlined, ToolOutlined } from "@ant-design/icons";
import { Alert, Button } from "antd";
import { useEffect, useState } from "react";
import axiosInstance from "../../api/axiosInstance";
import type { MyPortalSummary } from "../../service/implementation.service";

/**
 * In-app pointer to the group's onboarding portal.
 *
 * "Where's my onboarding link?" is the same support ticket this feature exists to
 * remove, so a group admin who is already logged in shouldn't have to dig through
 * their inbox for it. Renders nothing unless there's an unfinished portal.
 */
export default function SetupTrackerBanner() {
  const [summary, setSummary] = useState<MyPortalSummary | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axiosInstance
      .get<MyPortalSummary>("/implementation/my-portal/")
      .then(({ data }) => {
        if (!cancelled) setSummary(data);
      })
      .catch(() => {
        // Not every user has a portal, and this is a convenience — stay silent.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (dismissed || !summary?.has_portal || summary.is_complete) return null;

  const actionNeeded = summary.action_needed === true;

  return (
    <Alert
      type={actionNeeded ? "warning" : "info"}
      showIcon
      icon={<ToolOutlined />}
      closable
      onClose={() => setDismissed(true)}
      style={{ marginBottom: 16 }}
      message={
        actionNeeded
          ? "Your Nudge onboarding needs one thing from you"
          : `Nudge onboarding — ${summary.completed} of ${summary.total} steps done`
      }
      description={
        actionNeeded
          ? "Open your onboarding to see what we're waiting on."
          : "Nothing needed from you right now. Open it to see where we're up to."
      }
      action={
        <Button size="small" type="primary" href={summary.url} target="_blank" rel="noopener noreferrer">
          Open onboarding <ArrowRightOutlined />
        </Button>
      }
    />
  );
}
